import { memo, useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { termsSchema } from "../schemas";
import { useTranslation } from "react-i18next";

const CONTAINER_MIN_HEIGHT = 480;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };
const TERMS_SCROLL_STYLE = {
  maxHeight: "300px",
  overflowY: "auto" as const,
  fontSize: "0.9rem",
};

interface TermsData {
  isTermConditions: number;
  isDirectory: number;
}

interface TermsStepProps {
  defaultValues: TermsData;
  onNext: (data: TermsData) => void;
  onBack: () => void;
}

function TermsStepInner({
  defaultValues,
  onNext,
  onBack,
}: TermsStepProps) {
  const { t, i18n } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TermsData>({
    resolver: yupResolver(termsSchema),
    defaultValues,
  });

  const termsScrollContent = useMemo(() => {
    const activeLanguage = i18n.resolvedLanguage || i18n.language || "en";
    const effectiveDate = new Date().toLocaleDateString(activeLanguage);
    return (
      <div
        className="border rounded p-3 mb-2"
        style={TERMS_SCROLL_STYLE}
      >
        <strong>{t("membership.termsHeader")}</strong>
        <br />
        <em>{t("membership.effectiveDateLabel")}: {effectiveDate}</em>
        <br />
        <br />
        <strong>{t("membership.termsSection.renewalsTitle")}</strong>
        <br />
        {t("membership.termsSection.renewalsBody")}
        <br />
        <br />
        <strong>{t("membership.termsSection.cancelByMemberTitle")}</strong>
        <br />
        {t("membership.termsSection.cancelByMemberBody")}
        <br />
        <br />
        <strong>{t("membership.termsSection.cancelBySmbfTitle")}</strong>
        <br />
        {t("membership.termsSection.cancelBySmbfBody")}
        <br />
        <br />
        <div style={{ whiteSpace: "pre-line" }}>
          {t("membership.memberCharterLong")}
        </div>
        </div>
    );
  }, [t, i18n.language, i18n.resolvedLanguage]);

  const onSubmit = useCallback(
    (data: TermsData) => {
      onNext(data);
    },
    [onNext]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <div
        className="d-flex flex-column gap-3"
        style={CONTAINER_STYLE}
      >
        <h5>{t("membership.termsAndCharterTitle")}</h5>
        {termsScrollContent}
        <div className="d-flex flex-column px-3">
          <div>
            <Controller
              name="isTermConditions"
              control={control}
              render={({ field }) => (
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    id="terms2"
                    className="form-check-input"
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="terms2" className="form-check-label">
                    {t("membership.acceptTermsAndCharter")}
                  </label>
                </div>
              )}
            />
            {errors.isTermConditions && (
              <p className="text-danger mb-0 px-1">
                {errors.isTermConditions.message}
              </p>
            )}
          </div>
          <div className="mb-2">
            <Controller
              name="isDirectory"
              control={control}
              render={({ field }) => (
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    id="terms1"
                    className="form-check-input"
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="terms1" className="form-check-label">
                    {t("membership.cancellationNoticePolicy")}
                  </label>
                </div>
              )}
            />
            {errors.isDirectory && (
              <p className="text-danger mb-0 px-1">
                {errors.isDirectory.message}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
        >
          {t("auth.back")}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
        >
          {t("auth.continue")}
        </button>
      </div>
    </form>
  );
}

TermsStepInner.displayName = "TermsStep";

const TermsStep = memo(TermsStepInner);
TermsStep.displayName = "TermsStep";

export default TermsStep;
